import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'

import Box from '@material-ui/core/Box'
import Divider from '@material-ui/core/Divider'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles } from '@material-ui/core/styles'

import SaveAltIcon from '@material-ui/icons/SaveAlt'
import DeleteIcon from '@material-ui/icons/Delete'

import HeaderDetails from '../../components/HeaderDetails'
import { TransfersActionTypes } from '../../store/transfers/transfersTypes'
import { StoreState } from '../../store'
import {
  saveTransfer,
  removeTransfer,
} from '../../store/transfers/transfersActions'

import moment from 'moment'
import { setMask, applyMaskOnChange } from '../../utils'
import { Grid } from '@material-ui/core'
import GridTextField from '../../components/GridTextField'

const useStyles = makeStyles((theme) => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  content: {
    height: '100%',
    overflowY: 'scroll',
    padding: theme.spacing(2),
  },
  input: {
    margin: theme.spacing(1),
  },
  marginRight: {
    marginRight: theme.spacing(1),
  },
}))

const emptyForm = {
  value: '',
  date: moment().format('YYYY-MM-DD'),
  resourceOrigin: '',
  resourceDestiny: '',
  obs: '',
}

export default function TransfersDetails() {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { savingTransfer, removingTransfer, isEditing, originalData } =
    useSelector(({ transfersState }: StoreState) => ({
      savingTransfer: transfersState.savingTransfer,
      removingTransfer: transfersState.removingTransfer,
      isEditing: transfersState.selectedIndex !== null,
      originalData:
        transfersState.selectedIndex !== null
          ? transfersState.transfers[transfersState.selectedIndex]
          : null,
    }))

  const [isOpenModalRemove, setOpenModalRemove] = useState(false)
  const openModalRemove = () => setOpenModalRemove(true)
  const closeModalRemove = () => setOpenModalRemove(false)

  const form = useForm({
    defaultValues: !originalData
      ? emptyForm
      : {
          value: setMask('real', originalData.value.toFixed(2)),
          date: originalData.date,
          resourceOrigin: originalData.resourceOrigin,
          resourceDestiny: originalData.resourceDestiny,
          obs: originalData.obs,
        },
  })

  const resourceOrigin = form.watch('resourceOrigin')
  const resourceDestiny = form.watch('resourceDestiny')

  useEffect(() => {
    form.register({ name: 'resourceOrigin' })
    form.register({ name: 'resourceDestiny' })
  }, [form, originalData])

  const onClose = () => {
    dispatch({ type: TransfersActionTypes.EDITING_CANCEL })
  }

  const onSubmitForm = async () => {
    await form.triggerValidation()
    if (!resourceOrigin) {
      form.setError('resourceOrigin', '', 'Preencha este campo')
    }
    if (!resourceDestiny) {
      form.setError('resourceDestiny', '', 'Preencha este campo')
    } else if (resourceOrigin === resourceDestiny) {
      form.setError(
        'resourceDestiny',
        '',
        'O recurso de origem não pode ser igual ao destino'
      )
    }
    if (Object.entries(form.errors).length) {
      return
    }
    const data = form.getValues()
    dispatch(saveTransfer(data))
    form.reset(emptyForm)
  }

  const onClickConfirmRemoveTransfer = () => {
    closeModalRemove()
    dispatch(removeTransfer())
  }

  return (
    <Box className={classes.container}>
      <HeaderDetails
        title={isEditing ? 'Editando cadastro' : 'Novo cadastro'}
        onClose={onClose}
      />
      <div className={classes.content}>
        <Grid container spacing={2}>
          <GridTextField
            label="Valor"
            name="value"
            gridSize={6}
            inputRef={form.register({
              validate: (value) => {
                return (
                  !!parseFloat(
                    value.replace('R$ ', '').replace('.', '').replace(',', '.')
                  ) || 'Preencha um valor'
                )
              },
            })}
            errorMessage={form.errors?.value?.message}
            onChange={applyMaskOnChange('real')}
          />
          <GridTextField
            label="Data"
            name="date"
            type="date"
            gridSize={6}
            inputRef={form.register({
              required: 'Preencha a data',
            })}
            errorMessage={form.errors?.date?.message}
          />
          <GridTextField
            label="Recurso de origem"
            name="resourceOrigin"
            gridSize={6}
            options={['', 'Caixa', 'Conta', 'Outros']}
            onChange={(e: any) =>
              form.setValue('resourceOrigin', e.target.value)
            }
            errorMessage={form.errors?.resourceOrigin?.message}
            value={resourceOrigin}
          />
          <GridTextField
            label="Recurso de destino"
            name="resourceDestiny"
            gridSize={6}
            options={['', 'Caixa', 'Conta', 'Outros']}
            onChange={(e: any) =>
              form.setValue('resourceDestiny', e.target.value)
            }
            errorMessage={form.errors?.resourceDestiny?.message}
            value={resourceDestiny}
          />
          <GridTextField
            label="Observações"
            name="obs"
            gridSize={12}
            inputRef={form.register}
          />
        </Grid>
      </div>

      <Box>
        <Divider />
        <Box m={2}>
          <Button
            onClick={onSubmitForm}
            size="large"
            color="primary"
            variant="contained"
            disabled={savingTransfer}
            className={classes.marginRight}
          >
            <SaveAltIcon className={classes.marginRight} />
            {savingTransfer ? 'AGUARDE...' : 'SALVAR'}
          </Button>
          <Button
            onClick={openModalRemove}
            size="large"
            disabled={!isEditing}
            className={classes.marginRight}
          >
            <DeleteIcon className={classes.marginRight} />
            {removingTransfer ? 'AGUARDE...' : 'EXCLUIR'}
          </Button>
        </Box>
      </Box>

      <Dialog open={isOpenModalRemove} onClose={closeModalRemove}>
        <DialogTitle>Excluir este cadastro?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta ação não poderá ser desfeita.
            <br />
            Tem certeza que deseja remover este cadastro?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={removingTransfer}
            onClick={closeModalRemove}
            color="primary"
          >
            Não
          </Button>
          <Button
            disabled={removingTransfer}
            onClick={onClickConfirmRemoveTransfer}
            color="primary"
            autoFocus
          >
            SIM
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
